/**
 * PWS × AWARE Digital Partner Guide viewer — REDIRECT-ONLY (Feb 2026).
 *
 * The React iframe wrapper caused two production problems:
 *   1. The SPA fallback served `/pws-partner/guide` as the React shell,
 *      so any hiccup in JS bundle hydration surfaced as
 *      "You need to enable JavaScript to run this app".
 *   2. The download button pointed at `/api/pws-partner/guide.pdf`,
 *      which the backend 404s for anonymous callers when the feature
 *      flag isn't fully `enabled` — producing the browser's "Blocked"
 *      PDF-viewer state.
 *
 * Fix: the flipbook and PDF now live as PURE STATIC assets under
 *   /pws-partner/guide/index.html          (extracted flipbook)
 *   /pws-partner/guide/pages/page-01.webp  (all 23 pages)
 *   /pws-partner/PWS-AWARE-Partner-Guide.pdf (5.2 MB compressed PDF)
 *
 * This React component is now a thin redirect so that anyone landing
 * on `/pws-partner/guide` (no trailing slash) is bounced to the static
 * `/pws-partner/guide/index.html`, which the hosting layer serves
 * directly — never touching the React app.
 *
 * The route is unlisted (excluded from the sitemap) and the flipbook
 * HTML carries its own `<meta name="robots" content="noindex, nofollow">`.
 */
import { useEffect } from "react";

const STATIC_FLIPBOOK_URL = "/pws-partner/guide/index.html";

export default function PwsPartnerGuideViewer() {
  useEffect(() => {
    // Use `replace` so the redirect doesn't pollute the back button
    // history — pressing "back" from the flipbook returns the user to
    // wherever they came from (thank-you page / email link / QR).
    window.location.replace(STATIC_FLIPBOOK_URL);
  }, []);

  // Fallback link for the fraction of a second before the redirect
  // fires, and for JS-disabled browsers.
  return (
    <div
      data-testid="pws-guide-redirect"
      className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-200 p-8"
    >
      <div className="text-center">
        <p className="text-sm">Opening the PWS × AWARE Partner Guide…</p>
        <noscript>
          <p className="mt-4">
            If you are not redirected automatically,{" "}
            <a
              href={STATIC_FLIPBOOK_URL}
              className="text-cyan-300 underline"
              data-testid="pws-guide-redirect-fallback"
            >
              tap here to open the guide
            </a>.
          </p>
        </noscript>
      </div>
    </div>
  );
}
